import { create } from "@bufbuild/protobuf";
import { DataType } from "#gen/common/v1/types_pb.js";
import { BehaviorSchema, TriggerSchema } from "#gen/pages/v2/page_pb.js";
import type { Behavior, Page, Trigger } from "#gen/pages/v2/page_pb.js";
import SetAttributeAction from "#lib/components/flow/editor/builder/properties/page/actions/SetAttributeAction.svelte";
import StateTrigger from "#lib/components/flow/editor/builder/properties/page/triggers/StateTrigger.svelte";
import TimeoutTrigger from "#lib/components/flow/editor/builder/properties/page/triggers/TimeoutTrigger.svelte";
import { mountStateTrigger, mountTimeoutTrigger, setComponentAttribute } from "#lib/components/page/utils.js";
import type { Component as SvelteComponent } from "svelte";
import type { Writable } from "svelte/store";

export type ActionType = "setAttribute";

export type TriggerType = "timeout" | "state";

export interface BehaviorProps {
	page: Page;
	behavior: Behavior;
	onChange?: (behavior: Behavior) => void;
}

interface Item {
	name: string;
	iconSet: string;
	iconName: string;
	value: ActionType | TriggerType;
	accentColor: string;
	description: string;
	component: SvelteComponent<BehaviorProps>;
}

export interface ActionItem extends Item {
	do: (page: Writable<Page>, pageState: Writable<Record<string, unknown>>, behavior: Behavior) => void;
}

export interface TriggerItem extends Item {
	mount: (page: Writable<Page>, pageState: Writable<Record<string, unknown>>, behavior: Behavior) => (() => void) | undefined;
}

export const ActionTypes: ActionItem[] = [
	{
		name: "Set component attribute",
		iconSet: "heroicons",
		iconName: "code-bracket",
		value: "setAttribute" as ActionType,
		accentColor: "#B1CAF6",
		description: "Ex. Disable a component after a certain period of time",
		component: SetAttributeAction,
		do: setComponentAttribute,
	},
];

export const TriggerTypes: TriggerItem[] = [
	{
		name: "Timeout",
		iconSet: "heroicons",
		iconName: "clock",
		value: "timeout" as TriggerType,
		accentColor: "#B1CAF6",
		description: "Trigger the behavior after a certain period of time",
		component: TimeoutTrigger,
		mount: mountTimeoutTrigger,
	},
	{
		name: "Component state change",
		iconSet: "heroicons",
		iconName: "adjustments-horizontal",
		value: "state" as TriggerType,
		accentColor: "#B1CAF6",
		description: "Trigger the behavior when a component's state changes",
		component: StateTrigger,
		mount: mountStateTrigger,
	},
];

export const SettableAttributes = [
	{
		name: "Disabled",
		value: "disabled",
		dataType: DataType.BOOLEAN,
	},
	{
		name: "Hidden",
		value: "hidden",
		dataType: DataType.BOOLEAN,
	},
];

export function behaviorFor(type: ActionType): Behavior | undefined {
	switch (type) {
		case "setAttribute":
			return create(BehaviorSchema, {
				action: {
					value: {
						case: "setAttribute",
						value: {
							attribute: "",
						},
					},
				},
			});
		default:
			return undefined;
	}
}

export function triggerFor(type: TriggerType): Trigger | undefined {
	switch (type) {
		case "timeout":
			return create(TriggerSchema, {
				value: {
					case: "timeout",
					value: {
						timeout: "1s",
					},
				},
			});
		case "state":
			return create(TriggerSchema, {
				value: {
					case: "state",
					value: {},
				},
			});
		default:
			return undefined;
	}
}
